"use client";

import { faCommentPen } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ErrorMessage } from "@hookform/error-message";
import {
	Button,
	FileInputWithVisual,
	type FileInputWithVisualProps,
	Input,
	Select,
} from "@tosize/ui";
import { type ReactNode, useState } from "react";
import { useFormContext } from "react-hook-form";
import { match } from "ts-pattern";
import type { ContactOrdersType } from "~/app/actions/customer";

import { useQuery } from "~/hooks/useQuery";
import type { ContactProductTypes } from "../types/schema";

interface OrderNumberFieldProps {
	orderNumbers?: NonNullable<
		NonNullable<ContactOrdersType>["orders"]
	>["items"][number]["incrementId"][];
	messages: {
		incrementId: {
			label: string;
			showSelectLabel: string;
			hideSelectLabel: string;
		};
	};
}

export function OrderNumberFieldClient(props: OrderNumberFieldProps) {
	const { orderNumbers, messages } = props;
	const name = "order_number";
	const defaultShowSelect = !!orderNumbers?.length;
	const [showSelect, setShowSelect] = useState(defaultShowSelect);
	const { resetField } = useFormContext();

	function onShowChange() {
		setShowSelect(!showSelect);

		resetField(name, { defaultValue: !showSelect ? (orderNumbers?.at(0) ?? "") : "" });
	}

	return (
		<div>
			{showSelect ? (
				<Select label={messages.incrementId.label} name={name}>
					{orderNumbers?.map((orderNumber) => (
						<option value={orderNumber} key={orderNumber}>
							{orderNumber}
						</option>
					))}
				</Select>
			) : (
				<Input label={messages.incrementId.label} name={name} />
			)}

			{defaultShowSelect && (
				<Button variant="link" className="text-sm" onClick={() => onShowChange()}>
					{showSelect ? messages.incrementId.showSelectLabel : messages.incrementId.hideSelectLabel}
				</Button>
			)}
		</div>
	);
}

export function GeneralFeedbackClient({ title, children }: { title: string; children: ReactNode }) {
	const [show, setShow] = useState(false);

	return (
		<div className="flex items-center justify-center">
			{show ? (
				children
			) : (
				<button
					type="button"
					onClick={() => setShow(!show)}
					className="flex appearance-none items-center gap-4 text-start text-primary-medium enter:underline focus:outline-none"
				>
					<span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-shades-light text-dark">
						<FontAwesomeIcon icon={faCommentPen} />
					</span>

					<span>{title}</span>
				</button>
			)}
		</div>
	);
}

interface EmailInputProps {
	label: string;
	description?: string;
	invalidLabel: string;
}

export function EmailInput(props: EmailInputProps) {
	return (
		<Input
			label={props.label}
			description={props.description}
			type="email"
			autoComplete="email"
			name="email"
			registerOptions={{
				pattern: {
					value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
					message: props.invalidLabel,
				},
			}}
		/>
	);
}

export function ContactInputProductUrlList({ label }: { label: string }) {
	const methods = useFormContext();
	const name = "product_urls";
	const value: unknown = methods.watch(name);

	const lastItemIndex = Array.isArray(value)
		? Number(value.reduce((acc, item, index) => (item ? index + 1 : acc), 0))
		: 0;

	return (
		<div className="flex flex-col gap-4">
			{Array.from({ length: lastItemIndex + 1 }).map((_, index) => (
				<Input key={index} name={`${name}.${index}`} label={`${label} #${index + 1}`} />
			))}

			{lastItemIndex === 0 && (
				<div className="-mt-2 text-danger text-sm">
					<ErrorMessage name={`${name}.root`} />
				</div>
			)}
		</div>
	);
}

interface Props {
	types: ContactProductTypes[];
	children: ReactNode;
}

export function ShowProductTypeClient({ types, children }: Props) {
	const { watch } = useFormContext();
	const category: unknown = watch("product_type");

	if (typeof category !== "string" || !types.includes(category)) {
		return null;
	}

	return children;
}

type ProductTypes = "sheet" | "cabinet" | "accessory" | "sample";

export function ContactInputHiddenProductTypeClient({ type }: { type: ProductTypes }) {
	const { register } = useFormContext();

	return <input type="hidden" {...register("product_type", { value: type })} />;
}

export function ContactInputProductTypeClient(props: { label: string; children: ReactNode }) {
	const { searchParams } = useQuery();
	const productTypeParam = searchParams.get("product_type");

	return (
		<Select
			label={props.label}
			name="product_type"
			registerOptions={{ value: productTypeParam ?? "sheet" }}
		>
			{props.children}
		</Select>
	);
}

interface FileInputWithVisualClientProps {
	woodLabel: string;
	furnitureLabel: string;
}

export function FileInputWithVisualClient({
	woodLabel,
	furnitureLabel,
	...props
}: FileInputWithVisualClientProps & FileInputWithVisualProps) {
	const { watch } = useFormContext();
	const productType: unknown = watch("product_type");
	const subjectValue: unknown = watch("subject");

	let label = props.messages.label;

	if (subjectValue === "product_specifications_general") {
		label = woodLabel;
	}

	if (subjectValue === "product_specifications_furniture") {
		label = furnitureLabel;
	}

	if (typeof productType === "string") {
		label = match(productType)
			.with("sheet", () => woodLabel)
			.with("cabinet", () => furnitureLabel)
			.otherwise(() => props.messages.label);
	}

	return <FileInputWithVisual {...props} messages={{ ...props.messages, label }} />;
}
